import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RiderIcon } from '../../assets/images';
// Components
import { LCAlert } from '../../components/alert';
import { Anchor } from '../../components/anchor';
import App from '../../components/app';
import ErrorMessage from '../../components/error-message';
import EnhancedForm from '../../components/form';
import { FormDatas } from '../../local-types';
import { Logo } from '../../components/menu-drawer/menu-drawer';
import { AnchorBlock } from '../../components/anchorBlock';
import Listing from '../../components/listing';
// Modules
import { ErrorPayload, selectors as errorSelectors } from '../../redux/reducers/errors';
import {
    actions as loginActions,
    selectors as loginSelectors,
    types as loginTypes,
    User,
} from '../../redux/reducers/login';
import { selectors as pendingSelectors } from '../../redux/reducers/pendingOperations';
// Ressources
import UserUtils from '../../utils/userUtils';
import ObjectUtils from '../../utils/objectUtils';
import { IDRole } from '../../redux/reducers/api-types';
import DateUtils from '../../utils/dateUtils';
import StringUtils from '../../utils/stringUtils';

interface ProfilProps extends WithTranslation {
    user: User;
    getOrEditUserError: ErrorPayload;
    getUser: (id: number) => void;
    editUser: (id: number, email: string, firstname: string, lastname: string, phone_number: string) => void;
    editPassword: (id: number, oldPass: string, newPass: string) => void;
    isEditingUser: boolean;
    isEditingPassword: boolean;
}

interface ProfilState {
    formData: any;
    formPassword: any;
}

class ProfilPage extends React.Component<ProfilProps, ProfilState> {
    alertRef: any = React.createRef();

    state = {
        formData: {},
        formPassword: {
            password: '',
            confirmation: '',
            current_password: '',
        },
    };

    componentDidMount() {
        const user = UserUtils.getUser();
        this.props.getUser(user && user.user ? user.user.id : 0);
    }

    static getDerivedStateFromProps(props, state) {
        const { user } = props;
        const connectedUser = UserUtils.getUser();
        if (connectedUser && user && user.id === connectedUser.user.id) {
            return { formData: user };
        }
        return null;
    }

    generateGeneralInfosInputs = (): FormDatas[] => {
        const { t } = this.props;
        const inputs: FormDatas[] = [
            {
                title: t('common:form.general_infos'),
                sectionStyle: { flex: 1 },
                datas: [
                    { id: 'lastname', label: t('common:form.lastname'), required: true },
                    {
                        id: 'firstname',
                        label: t('common:form.firstname'),
                        required: true,
                    },
                    {
                        id: 'email',
                        label: t('common:form.email'),
                        type: 'email',
                        required: true,
                    },
                    {
                        id: 'phone_number',
                        label: t('common:form.phone_number'),
                        type: 'tel',
                        required: true,
                    },
                ],
            },
        ];
        return inputs;
    };

    generatePasswordInputs = (): FormDatas[] => {
        const { t, user } = this.props;
        const inputsPassword: FormDatas[] = [
            {
                title: t('password.title'),
                sectionStyle: { flex: 1 },
                datas: [
                    {
                        // A password form has to name the account it belongs to, otherwise the
                        // password manager saves the new password without knowing whose it is,
                        // and screen readers announce three password fields out of any context.
                        // Hidden because there is nothing to fill in here: the account is us.
                        id: 'username',
                        label: t('common:form.email'),
                        customView: () => (
                            <input
                                type="text"
                                name="username"
                                autoComplete="username"
                                value={user?.email ?? ''}
                                aria-hidden="true"
                                readOnly
                                hidden
                            />
                        ),
                    },
                    // autoComplete tells the password manager which field is which: it may
                    // fill the current one, and must not propose the saved password for the
                    // two others, which define the new one.
                    {
                        id: 'current_password',
                        label: t('password.current_password'),
                        hint: t('password.current_password_hint'),
                        type: 'password',
                        required: true,
                        inputsAttributs: { autoComplete: 'current-password' },
                    },
                    {
                        id: 'password',
                        label: t('password.new_password'),
                        hint: t('password.new_password_hint'),
                        type: 'password',
                        required: true,
                        inputsAttributs: { autoComplete: 'new-password' },
                    },
                    {
                        id: 'confirmation',
                        label: t('password.confirmation'),
                        hint: t('password.confirmation_hint'),
                        type: 'password',
                        required: true,
                        inputsAttributs: { autoComplete: 'new-password' },
                    },
                ],
            },
        ];
        return inputsPassword;
    };

    onSubmitGeneralInfos = (formData: any) => {
        const { user, editUser } = this.props;
        this.setState({
            formData: formData,
        });
        editUser(user.id, formData.email, formData.firstname, formData.lastname, formData.phone_number);
    };

    onSubmitPassword = (formData: any) => {
        const { user, editPassword } = this.props;
        this.setState({
            formPassword: formData,
        });
        if (formData.confirmation === formData.password) {
            this.alertRef.closeAlert();
            editPassword(user.id, formData.current_password, formData.password);
        } else {
            this.alertRef.showAlert();
        }
    };

    renderRolesView = (): JSX.Element => {
        const { t } = this.props;
        const connectedUser = UserUtils.getUser();
        if (connectedUser?.roles?.length <= 1) {
            return null;
        }
        return (
            <Listing
                nbColumns={3}
                datas={connectedUser.roles.map((item: IDRole, key) => (
                    <div key={key}>
                        <div>
                            <b>{item.name}</b> - {t('roles.granted_at')} {DateUtils.stringToDate(item.granted_at)}
                            {item.expires_at
                                ? ` - ${t('roles.expires_at')} ${DateUtils.stringToDate(item.expires_at)}`
                                : ''}
                        </div>
                    </div>
                ))}
            />
        );
    };

    renderRightsView = (): JSX.Element => {
        const { t } = this.props;
        const connectedUser = UserUtils.getUser();
        if (connectedUser?.rights && Object.entries(connectedUser.rights).length <= 1) {
            return null;
        }
        return (
            <Listing
                nbColumns={3}
                datas={Object.entries(connectedUser.rights).map(([tag, grant], key) => (
                    <div key={key}>
                        <div>
                            <b>{tag}</b> - {grant} ({t('rights.' + grant)})
                        </div>
                    </div>
                ))}
            />
        );
    };

    render() {
        const { t, user, getOrEditUserError } = this.props;
        const formPayload = this.state.formData;
        const formPasswordPayload = this.state.formPassword;

        return (
            <App
                noFav
                header={{
                    icon: Logo(RiderIcon, {}, 50),
                    title: StringUtils.userFullname(user),
                    anchors: [
                        { href: '#general_infos', label: t('common:form.general_infos') },
                        { href: '#password_infos', label: t('password.title') },
                        { href: '#roles_infos', label: t('roles.title') },
                    ],
                }}
            >
                <ErrorMessage payload={getOrEditUserError} />
                {!ObjectUtils.isEmpty(formPayload) && (
                    <>
                        <div style={{ display: 'flex' }}>
                            <Anchor id="general_infos" />
                            <EnhancedForm
                                payload={formPayload}
                                inputs={this.generateGeneralInfosInputs()}
                                onSubmit={this.onSubmitGeneralInfos}
                                loader={this.props.isEditingUser}
                            />
                            <Anchor id="password_infos" />
                            <EnhancedForm
                                payload={formPasswordPayload}
                                inputs={this.generatePasswordInputs()}
                                moreView={
                                    <LCAlert
                                        onRef={(ref) => (this.alertRef = ref)}
                                        details={t('password.password_different')}
                                        type="error"
                                    />
                                }
                                submitText={t('password.title')}
                                onSubmit={this.onSubmitPassword}
                                loader={this.props.isEditingPassword}
                            />
                        </div>
                        {UserUtils.hasRoleInternOrGod() && (
                            <>
                                <AnchorBlock id="roles_infos" title={t('roles.title')} items={this.renderRolesView()} />
                                <AnchorBlock
                                    id="rights_infos"
                                    title={t('rights.title')}
                                    items={this.renderRightsView()}
                                />
                            </>
                        )}
                    </>
                )}
            </App>
        );
    }
}

const ProfilPageWithTranslation = withTranslation(['profil', 'common'])(ProfilPage);

const mapStateToProps = (state) => ({
    user: loginSelectors.getUser(state),
    getOrEditUserError: errorSelectors.getOrEditUserError(state),
    isEditingUser: pendingSelectors.isPending(state, loginTypes.EDIT_USER),
    isEditingPassword: pendingSelectors.isPending(state, loginTypes.EDIT_PASSWORD),
});

const mapDispatchToProps = {
    getUser: loginActions.getUser,
    editUser: loginActions.editUser,
    editPassword: loginActions.editPassword,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfilPageWithTranslation);
